import React, { useContext } from 'react';
import { DeviceContext, DeviceProvider } from './DeviceContext';
import { ContactProvider } from './ContactContext';
import { AppointmentProvider } from './AppointmentContext';
import { ChatProvider } from './ChatContext';
import { MetricsProvider } from './MetricsContext';
import { DeviceAlertProvider } from './DeviceAlertContext';
import { DocumentProvider } from './DocumentContext';
import { MedicationProvider } from './MedicationContext';
import { PathwayProvider } from './PathwayContext';

interface DeviceContextProviderProps {
  children: React.ReactNode;
}

/**
 * A component that combines all the device-related context providers.
 * This provides the same functionality as the original DeviceContext,
 * but in a more modular way.
 */
const DeviceContextProvider: React.FC<DeviceContextProviderProps> = ({ children }) => {
  return (
    <DeviceProvider>
      <DeviceContextConsumer>{children}</DeviceContextConsumer>
    </DeviceProvider>
  );
};

/**
 * A component that consumes the DeviceContext and provides the device
 * to all the other context providers.
 */
const DeviceContextConsumer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { device } = useContext(DeviceContext);

  return (
    <ContactProvider device={device}>
      <AppointmentProvider device={device}>
        <ChatProvider device={device}>
          <MetricsProvider device={device}>
            <DeviceAlertProvider device={device}>
              <DocumentProvider device={device}>
                <MedicationProvider device={device}>
                  <PathwayProvider device={device}>{children}</PathwayProvider>
                </MedicationProvider>
              </DocumentProvider>
            </DeviceAlertProvider>
          </MetricsProvider>
        </ChatProvider>
      </AppointmentProvider>
    </ContactProvider>
  );
};

export default DeviceContextProvider;
